import {
  Box,
  Divider,
  FormControlLabel,
  Icon,
  List,
  ListItem,
  ListItemText,
  Stack,
  Switch,
  Typography,
  useTheme,
} from "@mui/material";
import { BarChartOutlined } from "@mui/icons-material";
import GlassCard from "Components/GlassCard/GlassCard";
import AnalyticsCardPanelHeader from "features/analytics/components/AnalyticsCardPanelHeader";
import { formatPercentage, percentageOf } from "Helpers/percentage";
import { metricTitles } from "Interfaces/enums";
import { useMemo, useState } from "react";
import { PolarGrid, Radar, RadarChart, ResponsiveContainer } from "recharts";
import { ReactComponent as ViewRate } from "Assets/Images/Stats/icon-view_rate.svg";
import { ReactComponent as Vistis } from "Assets/Images/Stats/icon-visits.svg";
import { ReactComponent as Shop } from "Assets/Images/Stats/icon-add-to-bag.svg";
import { ReactComponent as Sale } from "Assets/Images/Stats/icon-sale.svg";
import { styled } from "@mui/system";
import { ClickableTooltip } from "Components/ClickableTooltip";
import { StyledHelp } from "Components/ClickableTooltip/StyledHelp";
import { ApiDimension, ApiMetric } from "@incendium/api";
import { useChartData, useFormatMetric } from "features/analytics";
import { useSelectedProject } from "Hooks";
import { useFromToContext } from "Providers/FromToProvider";
import { TChartData } from "Interfaces";
import Loading from "Components/Loading/Loading";

interface IProductKeyStatsCardProps {
  productDimension: ApiDimension;
  selectedDimension: string;
}

const StyledIcon = styled(Icon)(({ theme }) => ({
  width: 30,
  height: 30,
  marginRight: theme.spacing(1),
  "& svg": {
    width: 30,
    height: 30,
    "& path:first-of-type": {
      fill: theme.palette.highlight,
    },
  },
}));

const StyledListItem = styled(ListItem)(({ theme }) => ({
  background: "transparent",
  "& .MuiTypography-root > .MuiTypography-root": {
    color: theme.palette.charts[1],
  },
  "& .MuiTypography-root:first-child > .MuiTypography-root": {
    color: theme.palette.charts[0],
  },
}));

function ProductKeyStatsCard({
  productDimension,
  selectedDimension,
}: IProductKeyStatsCardProps) {
  const theme = useTheme();
  const [useActiveProducts, setuseActiveProducts] = useState(true);
  const { selectedProject } = useSelectedProject();
  const { chartOutput } = useFromToContext();
  const formatMetric = useFormatMetric();

  const chart = useMemo(
    () => ({
      name: "",
      dimension: [productDimension],
      yAxisKeys: [
        {
          fields: [
            ApiMetric.METRIC_PRODUCT_IMPRESSIONS_COUNT,
            ApiMetric.METRIC_PAGE_ADD_TO_CART_CLICK_RATE,
            ApiMetric.METRIC_PRODUCT_PAGEVIEWS_COUNT,
            ApiMetric.METRIC_SALES_COUNT,
          ],
        },
      ],
      attributes: [],
      includeEmpty: false,
    }),
    [productDimension]
  );

  const { chartData, loading } = useChartData(
    selectedProject,
    chart,
    chartOutput,
    {
      ignoreModel: true,
    }
  );

  const data: TChartData | undefined = useMemo(() => {
    if (!chartData?.data) {
      return undefined;
    }
    return chartData?.data.find((d) => d.name === selectedDimension);
  }, [chartData, selectedDimension]);


  const average = useMemo(() => {
    return (chartData?.data || [])
      .filter(
        (d) => !useActiveProducts || Number(d[ApiMetric.METRIC_SALES_COUNT]) > 0
      )
      .reduce((acc: any, v, i, a) => {
        const { name, ...rest } = v;
        for (const key in rest) {
          if (!acc[key]) {
            acc[key] = 0;
          }
          acc[key] = acc[key] + Number(rest[key]) / a.length;
        }
        return acc;
      }, {});
  }, [useActiveProducts, chartData]);

  const differenceData = useMemo(() => {
    if (!average || !data) {
      return [];
    }

    return [
      {
        name: metricTitles.Impressions,
        average: 100,
        value:
          Number(data[ApiMetric.METRIC_PRODUCT_IMPRESSIONS_COUNT]) ===
            Number(average[ApiMetric.METRIC_PRODUCT_IMPRESSIONS_COUNT])
            ? 100
            : percentageOf(
              Number(data[ApiMetric.METRIC_PRODUCT_IMPRESSIONS_COUNT]) || 0,
              Number(average[ApiMetric.METRIC_PRODUCT_IMPRESSIONS_COUNT]) || 0
            ) || 0,
      },
      {
        name: metricTitles.Add_to_carts,
        average: 100,
        value:
          Number(data[ApiMetric.METRIC_PAGE_ADD_TO_CART_CLICK_RATE]) ===
            Number(average[ApiMetric.METRIC_PAGE_ADD_TO_CART_CLICK_RATE])
            ? 100
            : percentageOf(
              Number(data[ApiMetric.METRIC_PAGE_ADD_TO_CART_CLICK_RATE]) || 0,
              Number(average[ApiMetric.METRIC_PAGE_ADD_TO_CART_CLICK_RATE]) ||
              0
            ) || 0,
      },
      {
        name: metricTitles.Sales,
        average: 100,
        value:
          Number(data[ApiMetric.METRIC_SALES_COUNT]) ===
            Number(average[ApiMetric.METRIC_SALES_COUNT])
            ? 100
            : percentageOf(
              Number(data[ApiMetric.METRIC_SALES_COUNT]) || 0,
              Number(average[ApiMetric.METRIC_SALES_COUNT]) || 0
            ) || 0,
      },
      {
        name: metricTitles.PageVistits,
        average: 100,
        value:
          Number(data[ApiMetric.METRIC_PRODUCT_PAGEVIEWS_COUNT]) ===
            Number(average[ApiMetric.METRIC_PRODUCT_PAGEVIEWS_COUNT])
            ? 100
            : percentageOf(
              Number(data[ApiMetric.METRIC_PRODUCT_PAGEVIEWS_COUNT]) || 0,
              Number(average[ApiMetric.METRIC_PRODUCT_PAGEVIEWS_COUNT]) || 0
            ) || 0,
      },
    ];
  }, [average, data]);

  return (
    <GlassCard
      noShadow
      boxProps={{
        px: 3,
        py: 2,
      }}
    >
      <Stack spacing={1.5} mb={1}>
        <AnalyticsCardPanelHeader
          icon={<BarChartOutlined fontSize="small" />}
          title="Key Statistics"
          action={
            <Stack direction="row" alignItems="center" spacing={0.5}>
              <FormControlLabel
                sx={{ margin: 0 }}
                control={
                  <Switch
                    checked={useActiveProducts}
                    onChange={(e, checked) => setuseActiveProducts(checked)}
                  />
                }
                label="Active"
              />
              <ClickableTooltip
                text={
                  "select active to compare with all products that have had a sale in selected time period."
                }
                placement="right"
                icon={<StyledHelp fontSize="small" />}
              />
            </Stack>
          }
        />
        <Typography variant="body2" color={"secondary"}>
          See how this product{" "}
          {productDimension === ApiDimension.DIMENSION_PRODUCT_CATEGORY &&
            "category"}{" "}
          compares to average product{" "}
          {productDimension === ApiDimension.DIMENSION_PRODUCT_CATEGORY &&
            "category"}{" "}
          performance
        </Typography>
      </Stack>
      <Divider />
      <Stack
        py={1.5}
        alignItems="center"
        justifyContent={"center"}
        sx={{ minHeight: 309 }}
      >
        {loading ? (
          <Loading />
        ) : (
          <>
            <Typography color={"interest"} mb={1}>
              as percentage of site average
            </Typography>
            <Stack
              direction={"row"}
              sx={{ width: "100%" }}
              justifyContent={"center"}
            >
              <Box
                sx={{
                  minWidth: 45,
                  maxWidth: 45,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "flex-end",
                }}
              >
                <Box>
                  <Typography variant="body2" align="center">
                    page visits
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ color: theme.palette.charts[0] }}
                    align="center"
                    mr={1}
                  >
                    {formatPercentage(
                      differenceData.find(
                        (d) => d.name === metricTitles.PageVistits
                      )?.value || 0
                    )}
                  </Typography>
                </Box>
              </Box>

              <Stack
                sx={{
                  flexGrow: 1,
                  position: "relative",
                }}
                alignItems="center"
              >
                <Box
                  sx={{
                    position: "absolute",
                    paddingTop: "100%",
                    width: "100%",
                    top: "50%",
                    left: "50%",
                    borderRadius: "50%",
                    transform: "translate(-50%, -50%)",
                    background: "rgba(255,255,255,0.5)",
                    boxShadow: `-3px 3px 10px #5E65E54D`,
                  }}
                />
                <Box
                  sx={{
                    position: "absolute",
                    height: 112,
                    width: 112,
                    top: "50%",
                    left: "50%",
                    borderRadius: "50%",
                    transform: "translate(-50%, -50%)",
                    background: "rgba(255,255,255,0.5)",
                    boxShadow: `-3px 3px 10px #5E65E54D`,
                  }}
                />
                <Box
                  sx={{
                    position: "absolute",
                    height: 18,
                    width: 18,
                    top: "50%",
                    left: "50%",
                    borderRadius: "50%",
                    transform: "translate(-50%, -50%)",
                    background: "rgba(255,255,255,0.5)",
                    boxShadow: `-3px 3px 10px #5E65E54D`,
                  }}
                />

                <Stack>
                  <Typography variant="body2" align="center">
                    {metricTitles.Impressions}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ color: theme.palette.charts[0] }}
                    align="center"
                    mb={1}
                  >
                    {formatPercentage(
                      differenceData.find(
                        (d) => d.name === metricTitles.Impressions
                      )?.value || 0
                    )}
                  </Typography>
                </Stack>
                <Box
                  sx={{
                    width: "100%",
                    aspectRatio: "1/1",
                  }}
                >
                  <ResponsiveContainer width="100%" height="100%">
                    <RadarChart
                      cx="50%"
                      cy="50%"
                      outerRadius="100%"
                      data={differenceData}
                    >
                      <PolarGrid
                        gridType="circle"
                        cx={0}
                        cy={0}
                        polarRadius={[]}
                        stroke="#EAEAEA"
                      />

                      <Radar
                        name="Average"
                        dataKey="average"
                        stroke={theme.palette.charts[1]}
                        strokeWidth={2}
                        fillOpacity={0}
                      />
                      <Radar
                        name="Current"
                        dataKey="value"
                        stroke={theme.palette.charts[0]}
                        strokeWidth={2}
                        fillOpacity={0}
                      />
                    </RadarChart>
                  </ResponsiveContainer>
                </Box>
                <Box>
                  <Typography variant="body2" mt={1} align="center">
                    sales
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ color: theme.palette.charts[0] }}
                    align="center"
                  >
                    {formatPercentage(
                      differenceData.find((d) => d.name === metricTitles.Sales)
                        ?.value || 0
                    )}
                  </Typography>
                </Box>
              </Stack>

              <Box
                sx={{
                  minWidth: 45,
                  maxWidth: 45,
                  display: "flex",
                  alignItems: "center",
                }}
              >
                <Box>
                  <Typography variant="body2" ml={1} align="center">
                    add to cart rate
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ color: theme.palette.charts[0] }}
                    align="center"
                  >
                    {formatPercentage(
                      differenceData.find(
                        (d) => d.name === metricTitles.Add_to_carts
                      )?.value || 0
                    )}
                  </Typography>
                </Box>
              </Box>
            </Stack>
          </>
        )}
      </Stack>
      <Divider />
      <List>
        <StyledListItem disablePadding>
          <StyledIcon>
            <ViewRate />
          </StyledIcon>
          <ListItemText
            primary={
              <Typography>
                Product Impressions:{" "}
                <Typography fontWeight={"bold"} display={"inline"}>
                  {formatMetric(
                    ApiMetric.METRIC_PRODUCT_IMPRESSIONS_COUNT,
                    data
                      ? Number(data[ApiMetric.METRIC_PRODUCT_IMPRESSIONS_COUNT])
                      : 0
                  )}
                </Typography>
              </Typography>
            }
            secondary={
              <Typography variant="body2">
                Site Average:{" "}
                <Typography
                  variant="body2"
                  fontWeight={"bold"}
                  display={"inline"}
                >
                  {formatMetric(
                    ApiMetric.METRIC_PRODUCT_IMPRESSIONS_COUNT,
                    Number(
                      average[ApiMetric.METRIC_PRODUCT_IMPRESSIONS_COUNT]
                    ) || 0
                  )}
                </Typography>
              </Typography>
            }
          />
        </StyledListItem>
        <StyledListItem disablePadding>
          <StyledIcon>
            <Vistis />
          </StyledIcon>
          <ListItemText
            primary={
              <Typography>
                Page Visits:{" "}
                <Typography fontWeight={"bold"} display={"inline"}>
                  {formatMetric(
                    ApiMetric.METRIC_PRODUCT_PAGEVIEWS_COUNT,
                    data
                      ? Number(data[ApiMetric.METRIC_PRODUCT_PAGEVIEWS_COUNT])
                      : 0
                  )}
                </Typography>
              </Typography>
            }
            secondary={
              <Typography variant="body2">
                Site Average:{" "}
                <Typography
                  variant="body2"
                  fontWeight={"bold"}
                  display={"inline"}
                >
                  {formatMetric(
                    ApiMetric.METRIC_PRODUCT_PAGEVIEWS_COUNT,
                    Number(average[ApiMetric.METRIC_PRODUCT_PAGEVIEWS_COUNT]) ||
                    0
                  )}
                </Typography>
              </Typography>
            }
          />
        </StyledListItem>
        <StyledListItem disablePadding>
          <StyledIcon>
            <Shop />
          </StyledIcon>
          <ListItemText
            primary={
              <Typography>
                Add to Cart Rate:{" "}
                <Typography fontWeight={"bold"} display={"inline"}>
                  {formatMetric(
                    ApiMetric.METRIC_PAGE_ADD_TO_CART_CLICK_RATE,
                    data
                      ? Number(
                        data[ApiMetric.METRIC_PAGE_ADD_TO_CART_CLICK_RATE]
                      )
                      : 0
                  )}
                </Typography>
              </Typography>
            }
            secondary={
              <Typography variant="body2">
                Site Average:{" "}
                <Typography
                  variant="body2"
                  fontWeight={"bold"}
                  display={"inline"}
                >
                  {formatMetric(
                    ApiMetric.METRIC_PAGE_ADD_TO_CART_CLICK_RATE,
                    Number(
                      average[ApiMetric.METRIC_PAGE_ADD_TO_CART_CLICK_RATE]
                    ) || 0
                  )}
                </Typography>
              </Typography>
            }
          />
        </StyledListItem>
        <StyledListItem disablePadding>
          <StyledIcon>
            <Sale />
          </StyledIcon>
          <ListItemText
            primary={
              <Typography>
                Sales:{" "}
                <Typography fontWeight={"bold"} display={"inline"}>
                  {formatMetric(
                    ApiMetric.METRIC_SALES_COUNT,
                    data ? Number(data[ApiMetric.METRIC_SALES_COUNT]) : 0
                  )}
                </Typography>
              </Typography>
            }
            secondary={
              <Typography variant="body2">
                Site Average:{" "}
                <Typography
                  variant="body2"
                  fontWeight={"bold"}
                  display={"inline"}
                >
                  {formatMetric(
                    ApiMetric.METRIC_SALES_COUNT,
                    Number(average[ApiMetric.METRIC_SALES_COUNT]) || 0
                  )}
                </Typography>
              </Typography>
            }
          />
        </StyledListItem>
      </List>
    </GlassCard>
  );
}

export default ProductKeyStatsCard;
